// frontend/src/services/billingService.ts
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 *
 * AUDITORIA: Serviço de billing (Fase 3 — Agentes Campeões).
 * Interface com /api/billing do backend: planos, assinatura, quotas.
 * O projeto vem da sessão no backend — NUNCA enviado pelo cliente.
 */

import api from './api';
import { createLogger, executeWithErrorHandling } from '../utils/logger';

const logger = createLogger('BillingService');

// =============================================
// TYPES & INTERFACES (espelham IBilling.ts do backend)
// =============================================

export type BillingCycle = 'MONTHLY' | 'YEARLY';
export type PaymentMethodType = 'CREDIT_CARD' | 'PIX' | 'BOLETO';
export type SubscriptionStatus = 'sem_assinatura' | 'trial' | 'ativa' | 'suspensa' | 'cancelada';

export interface PlanResources {
  max_agentes?: number;
  max_mensagens?: number;
  max_intencoes?: number;
  [key: string]: number | undefined;
}

export interface Plan {
  id: string;
  nome: string;
  descricao: string;
  preco_mensal: number;
  preco_anual: number;
  trial_dias: number;
  recursos: PlanResources;
  visivel: boolean;
  ativo: boolean;
}

export interface Subscription {
  id: string;
  projeto_id: string;
  plano_id: string;
  status: SubscriptionStatus;
  ciclo: BillingCycle;
  metodo_pagamento: PaymentMethodType;
  trial_inicio?: string | null;
  trial_fim?: string | null;
  data_proxima_cobranca?: string | null;
  valor_atual?: number | null;
}

export interface SubscriptionWithPlan {
  assinatura: Subscription;
  plano: Plan;
}

export interface QuotaMetric {
  usado: number;
  limite: number; // -1 = ilimitado; 0 = sem plano
  percentual: number;
}

export interface QuotaUsage {
  mensagens: QuotaMetric;
  agentes: QuotaMetric;
  resetEm: string;
}

export type InvoiceStatus = 'pendente' | 'paga' | 'vencida' | 'cancelada' | 'estornada';

export interface Invoice {
  id: string;
  numero_fatura: string;
  valor_liquido: number;
  status: InvoiceStatus;
  data_vencimento: string;
  data_pagamento: string | null;
  asaas_url_boleto: string | null;
  asaas_url_invoice: string | null;
}

export interface StartSubscriptionInput {
  planId: string;
  ciclo: BillingCycle;
  metodoPagamento: PaymentMethodType;
  cardToken?: string;
  cpfCnpj?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// =============================================
// BILLING SERVICE
// =============================================

export const billingService = {
  /**
   * Lista planos visíveis — endpoint PÚBLICO (usado na landing).
   */
  listPlans: async (): Promise<Plan[]> => {
    return executeWithErrorHandling(
      async () => {
        const { data } = await api.get<ApiResponse<Plan[]>>('/api/billing/plans');
        return data.data;
      },
      logger,
      'listar planos'
    );
  },

  /**
   * Assinatura atual do projeto da sessão (null = nunca assinou).
   */
  getSubscription: async (): Promise<SubscriptionWithPlan | null> => {
    return executeWithErrorHandling(
      async () => {
        const { data } = await api.get<ApiResponse<SubscriptionWithPlan | null>>('/api/billing/subscription');
        return data.data;
      },
      logger,
      'buscar assinatura'
    );
  },

  /**
   * Inicia trial (se elegível) ou assinatura paga via Asaas.
   */
  startSubscription: async (input: StartSubscriptionInput): Promise<Subscription> => {
    return executeWithErrorHandling(
      async () => {
        logger.info('Iniciando assinatura', { planId: input.planId, ciclo: input.ciclo });
        const { data } = await api.post<ApiResponse<Subscription>>('/api/billing/subscription', input);
        logger.info('Assinatura iniciada', { status: data.data.status });
        return data.data;
      },
      logger,
      'iniciar assinatura'
    );
  },

  /**
   * Cancela a assinatura. immediate=true cancela no gateway na hora;
   * false mantém até o fim do ciclo pago.
   */
  cancelSubscription: async (immediate = false): Promise<void> => {
    return executeWithErrorHandling(
      async () => {
        await api.delete<ApiResponse<null>>(`/api/billing/subscription?immediate=${immediate}`);
        logger.info('Assinatura cancelada', { immediate });
      },
      logger,
      'cancelar assinatura'
    );
  },

  /**
   * Upgrade/downgrade de plano (assinatura ativa).
   */
  changePlan: async (planId: string): Promise<Subscription> => {
    return executeWithErrorHandling(
      async () => {
        const { data } = await api.put<ApiResponse<Subscription>>('/api/billing/subscription/plan', { planId });
        return data.data;
      },
      logger,
      'alterar plano'
    );
  },

  /**
   * Uso de quotas do mês com percentuais e data de reset.
   */
  getUsage: async (): Promise<QuotaUsage> => {
    return executeWithErrorHandling(
      async () => {
        const { data } = await api.get<ApiResponse<QuotaUsage>>('/api/billing/usage');
        return data.data;
      },
      logger,
      'buscar uso de quotas'
    );
  },

  /**
   * Lista faturas do projeto da sessão (paginado).
   */
  getInvoices: async (page = 1, limit = 20): Promise<Invoice[]> => {
    return executeWithErrorHandling(
      async () => {
        const { data } = await api.get<ApiResponse<Invoice[]>>(
          `/api/billing/invoices?page=${page}&limit=${limit}`
        );
        return data.data;
      },
      logger,
      'listar faturas'
    );
  },

  /**
   * Formata preço em reais (planos gratuitos → "Grátis").
   */
  formatPrice: (valor: number): string => {
    if (valor === 0) return 'Grátis';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  },

  /**
   * Descreve um limite de recurso (-1 = ilimitado).
   * ilimitadoLabel permite concordância de gênero ("Mensagens ilimitadas").
   */
  formatLimit: (
    limite: number | undefined,
    singular: string,
    plural: string,
    ilimitadoLabel?: string
  ): string => {
    if (limite === undefined || limite === -1) return ilimitadoLabel ?? `${plural} ilimitados`;
    if (limite === 1) return `1 ${singular}`;
    return `${limite.toLocaleString('pt-BR')} ${plural}`;
  },
};
